import { createContext, useContext, useState, useCallback, useEffect } from 'react';

export type ExplanationStyle = 'Simple' | 'Detailed' | 'Exam Style' | 'Step-by-Step' | 'Beginner Friendly';
export type VoiceStyle = 'Calm' | 'Professional' | 'Energetic' | 'Tutor Style' | 'Student Friendly';
export type NarrationSpeed = 'Slow' | 'Normal' | 'Fast';
export type LearningMode = 'Homework Help' | 'Exam Revision' | 'Learn New Topic' | 'Quick Summary' | 'Deep Study';
export type LearningStyle = 'Visual' | 'Fast' | 'Detailed' | 'Exam Prep' | 'Balanced';

export interface AIPreferencesState {
  explanationStyle: ExplanationStyle;
  voiceStyle: VoiceStyle;
  narrationSpeed: NarrationSpeed;
  learningMode: LearningMode;
  preferredSubjects: string[];
  learningStyle: LearningStyle;
  tutorMode: 'patient' | 'direct' | 'encouraging';
  showConfidenceIndicators: boolean;
  autoLearningMode: boolean;
}

const STORAGE_KEY = 'lavyn_ai_preferences_v2';

const defaultPreferences: AIPreferencesState = {
  explanationStyle: 'Step-by-Step',
  voiceStyle: 'Tutor Style',
  narrationSpeed: 'Normal',
  learningMode: 'Homework Help',
  preferredSubjects: [],
  learningStyle: 'Balanced',
  tutorMode: 'patient',
  showConfidenceIndicators: true,
  autoLearningMode: true,
};

function loadFromStorage(): AIPreferencesState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<AIPreferencesState>;
      return { ...defaultPreferences, ...parsed };
    }
  } catch {
    // ignore
  }
  return { ...defaultPreferences };
}

function saveToStorage(prefs: AIPreferencesState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // ignore
  }
}

interface AIPreferencesContextValue {
  preferences: AIPreferencesState;
  setExplanationStyle: (style: ExplanationStyle) => void;
  setVoiceStyle: (style: VoiceStyle) => void;
  setNarrationSpeed: (speed: NarrationSpeed) => void;
  setLearningMode: (mode: LearningMode) => void;
  setPreferredSubjects: (subjects: string[]) => void;
  setLearningStyle: (style: LearningStyle) => void;
  setTutorMode: (mode: 'patient' | 'direct' | 'encouraging') => void;
  setShowConfidenceIndicators: (show: boolean) => void;
  setAutoLearningMode: (auto: boolean) => void;
  resetToDefaults: () => void;
}

const AIPreferencesContext = createContext<AIPreferencesContextValue | null>(null);

export function AIPreferencesProvider({ children }: { children: React.ReactNode }) {
  const [preferences, setPreferences] = useState<AIPreferencesState>(loadFromStorage);

  useEffect(() => {
    saveToStorage(preferences);
  }, [preferences]);

  const setExplanationStyle = useCallback((style: ExplanationStyle) => {
    setPreferences((p) => ({ ...p, explanationStyle: style }));
  }, []);

  const setVoiceStyle = useCallback((style: VoiceStyle) => {
    setPreferences((p) => ({ ...p, voiceStyle: style }));
  }, []);

  const setNarrationSpeed = useCallback((speed: NarrationSpeed) => {
    setPreferences((p) => ({ ...p, narrationSpeed: speed }));
  }, []);

  const setLearningMode = useCallback((mode: LearningMode) => {
    setPreferences((p) => ({ ...p, learningMode: mode }));
  }, []);

  const setPreferredSubjects = useCallback((subjects: string[]) => {
    setPreferences((p) => ({ ...p, preferredSubjects: subjects }));
  }, []);

  const setLearningStyle = useCallback((style: LearningStyle) => {
    setPreferences((p) => ({ ...p, learningStyle: style }));
  }, []);

  const setTutorMode = useCallback((mode: 'patient' | 'direct' | 'encouraging') => {
    setPreferences((p) => ({ ...p, tutorMode: mode }));
  }, []);

  const setShowConfidenceIndicators = useCallback((show: boolean) => {
    setPreferences((p) => ({ ...p, showConfidenceIndicators: show }));
  }, []);

  const setAutoLearningMode = useCallback((auto: boolean) => {
    setPreferences((p) => ({ ...p, autoLearningMode: auto }));
  }, []);

  const resetToDefaults = useCallback(() => {
    setPreferences({ ...defaultPreferences });
  }, []);

  return (
    <AIPreferencesContext.Provider
      value={{
        preferences,
        setExplanationStyle,
        setVoiceStyle,
        setNarrationSpeed,
        setLearningMode,
        setPreferredSubjects,
        setLearningStyle,
        setTutorMode,
        setShowConfidenceIndicators,
        setAutoLearningMode,
        resetToDefaults,
      }}
    >
      {children}
    </AIPreferencesContext.Provider>
  );
}

export function useAIPreferences() {
  const ctx = useContext(AIPreferencesContext);
  if (!ctx) {
    throw new Error('useAIPreferences must be used within AIPreferencesProvider');
  }
  return ctx;
}

/* ── Helper utilities for AI generation components ── */

export function getExplanationStyleDescription(style: ExplanationStyle): string {
  switch (style) {
    case 'Simple': return 'Shorter explanations with faster summaries and key takeaways.';
    case 'Detailed': return 'Deeper breakdowns with more reasoning, examples, and context.';
    case 'Exam Style': return 'Concise, exam-focused answers with marking guidance.';
    case 'Step-by-Step': return 'Numbered learning structure with detailed calculations and guided explanations.';
    case 'Beginner Friendly': return 'Foundational concepts explained with analogies and minimal jargon.';
    default: return '';
  }
}

export function getVoiceStyleDescription(style: VoiceStyle): string {
  switch (style) {
    case 'Calm': return 'Relaxed and soothing narration pace.';
    case 'Professional': return 'Formal, clear, and authoritative delivery.';
    case 'Energetic': return 'Upbeat, encouraging, and dynamic tone.';
    case 'Tutor Style': return 'Personal, patient, one-on-one teaching feel.';
    case 'Student Friendly': return 'Casual, relatable, like a smart study buddy.';
    default: return '';
  }
}

export function getLearningModeDescription(mode: LearningMode): string {
  switch (mode) {
    case 'Homework Help': return 'Focus on solving specific problems step-by-step.';
    case 'Exam Revision': return 'Concise revision notes and exam-focused summaries.';
    case 'Learn New Topic': return 'Complete introduction with foundations and examples.';
    case 'Quick Summary': return 'Fast overviews of key concepts only.';
    case 'Deep Study': return 'Thorough explanations with connections and depth.';
    default: return '';
  }
}

export function getNarrationSpeedValue(speed: NarrationSpeed): number {
  switch (speed) {
    case 'Slow': return 0.75;
    case 'Normal': return 1;
    case 'Fast': return 1.25;
    default: return 1;
  }
}

export function getLearningStyleDescription(style: LearningStyle): string {
  switch (style) {
    case 'Visual': return 'Prefers diagrams, charts, and visual explanations.';
    case 'Fast': return 'Wants quick summaries and key takeaways only.';
    case 'Detailed': return 'Enjoys thorough explanations with rich examples.';
    case 'Exam Prep': return 'Focused on exam technique and past paper style.';
    case 'Balanced': return 'Adapts between all learning styles as needed.';
    default: return '';
  }
}

export function getTutorModeDescription(mode: string): string {
  switch (mode) {
    case 'patient': return 'Takes time to explain, checks understanding, never rushes.';
    case 'direct': return 'Gets straight to the point with minimal fluff.';
    case 'encouraging': return 'Builds confidence with positive reinforcement and praise.';
    default: return '';
  }
}

export function getAnalyzingSteps(mode: LearningMode, outputType: 'text' | 'video') {
  const baseSteps = {
    text: [
      { message: 'Analyzing your input', detail: 'Reading text, equations, and diagrams' },
      { message: 'Extracting key concepts', detail: 'Identifying problems and subject area' },
      { message: 'Generating explanation', detail: 'Writing clear explanations with structured steps' },
      { message: 'Preparing study materials', detail: 'Creating notes, summaries, and quizzes' },
      { message: 'Finalizing results', detail: 'Formatting and optimizing for your preferences' },
    ],
    video: [
      { message: 'Understanding your work', detail: 'Reading the question, equations, and subject clues' },
      { message: 'Building the visual lesson', detail: 'Planning one continuous study page' },
      { message: 'Creating diagrams', detail: 'Choosing useful subject-aware visuals and labels' },
      { message: 'Preparing your explanation', detail: 'Connecting the working, narration, and visual beats' },
      { message: 'Almost ready', detail: 'Checking chapters, camera movement, and key takeaways' },
    ],
  };

  const modeSteps: Record<LearningMode, { message: string; detail: string }[]> = {
    'Homework Help': [
      { message: 'Reading the problem', detail: 'Extracting equations and known values' },
      { message: 'Planning the solution', detail: 'Identifying the right method to solve' },
      { message: 'Working through steps', detail: 'Showing every calculation clearly' },
      { message: 'Verifying the answer', detail: 'Double-checking logic and units' },
      { message: 'Packaging the solution', detail: 'Formatting for easy studying' },
    ],
    'Exam Revision': [
      { message: 'Scanning for key topics', detail: 'Identifying exam-relevant concepts' },
      { message: 'Building revision notes', detail: 'Creating concise, high-yield summaries' },
      { message: 'Highlighting common traps', detail: 'Marking mistakes students often make' },
      { message: 'Adding exam tips', detail: 'Including time-saving strategies' },
      { message: 'Finalizing revision pack', detail: 'Optimizing for quick review before exams' },
    ],
    'Learn New Topic': [
      { message: 'Mapping the topic', detail: 'Understanding prerequisites and structure' },
      { message: 'Building foundations', detail: 'Introducing core concepts with analogies' },
      { message: 'Adding worked examples', detail: 'Showing how concepts apply in practice' },
      { message: 'Creating connections', detail: 'Linking to related topics you know' },
      { message: 'Finalizing lesson', detail: 'Structuring for smooth learning flow' },
    ],
    'Quick Summary': [
      { message: 'Identifying essentials', detail: 'Extracting only the most important parts' },
      { message: 'Condensing explanations', detail: 'Removing extra detail, keeping key facts' },
      { message: 'Building bullet summaries', detail: 'Creating scannable takeaways' },
      { message: 'Adding quick facts', detail: 'One-liner reminders for each concept' },
      { message: 'Finalizing summary', detail: 'Optimizing for fastest possible review' },
    ],
    'Deep Study': [
      { message: 'Analyzing in depth', detail: 'Breaking down every component thoroughly' },
      { message: 'Building theory layer', detail: 'Explaining why things work, not just how' },
      { message: 'Adding rich examples', detail: 'Multiple scenarios and edge cases' },
      { message: 'Creating connections', detail: 'Linking to broader subject knowledge' },
      { message: 'Finalizing deep lesson', detail: 'Structuring for comprehensive understanding' },
    ],
  };

  return modeSteps[mode] || baseSteps[outputType];
}